import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';

interface Rule { id: string; customerId: string; kind: 'title_regex' | 'entity_equals' | 'severity_below'; value: string; description?: string | null; enabled: boolean; createdBy?: string | null; createdAt?: string; }

const KIND_OPTIONS: { value: Rule['kind']; label: string; placeholder: string }[] = [
  { value: 'title_regex',    label: 'Title matches regex',     placeholder: 'e.g. ^Test alert' },
  { value: 'entity_equals',  label: 'Entity name equals',      placeholder: 'e.g. svc-backup@contoso.com' },
  { value: 'severity_below', label: 'Severity at-or-below',    placeholder: 'Informational | Low | Medium' },
];

export function SuppressionRulesEditor() {
  const { customers, activeCustomerId, setActiveCustomerId } = useCustomers();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ kind: Rule['kind']; value: string; description: string }>({ kind: 'title_regex', value: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ rules: Rule[] }>(`/suppression?customerId=${encodeURIComponent(activeCustomerId)}`);
      setRules(data.rules);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!draft.value.trim()) return;
    setError(null);
    try {
      await apiFetch('/suppression', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, kind: draft.kind, value: draft.value.trim(), description: draft.description.trim() || undefined }),
      });
      setDraft({ kind: 'title_regex', value: '', description: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add failed');
    }
  }

  async function toggle(rule: Rule) {
    try {
      await apiFetch(`/suppression/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !rule.enabled }),
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function remove(rule: Rule) {
    if (!confirm('Delete this suppression rule?')) return;
    try {
      await apiFetch(`/suppression/${rule.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">Suppression Rules</h3>
        <select className="input-default text-xs" value={activeCustomerId} onChange={(e) => setActiveCustomerId(e.target.value)}>
          {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
      </div>
      <p className="text-xs text-muted-foreground">Suppressed incidents are filtered out of /dashboard-data and the global new-incident watcher. Toggling a rule off restores them on the next refresh (within 30s cache).</p>

      {error && <div className="px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}

      <div className="grid grid-cols-[1fr_2fr_2fr_auto] gap-2 items-end p-3 rounded-lg border border-dashed border-border bg-muted/20">
        <label className="text-xs text-muted-foreground">
          Rule type
          <select className="input-default mt-1 w-full" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as Rule['kind'] })}>
            {KIND_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Value
          <input className="input-default mt-1 w-full font-mono" placeholder={KIND_OPTIONS.find((o) => o.value === draft.kind)?.placeholder} value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
        </label>
        <label className="text-xs text-muted-foreground">
          Description (optional)
          <input className="input-default mt-1 w-full" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </label>
        <button onClick={add} disabled={!draft.value.trim()} className="text-xs font-semibold px-3 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 self-end">Add</button>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Value</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Created by</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-1.5">{KIND_OPTIONS.find((o) => o.value === r.kind)?.label || r.kind}</td>
                <td className="px-3 py-1.5 font-mono break-all">{r.value}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.description || '-'}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.createdBy || '-'}</td>
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.enabled ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>{r.enabled ? 'ENABLED' : 'DISABLED'}</span>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button onClick={() => toggle(r)} className="text-xs px-2 py-1 mr-1 rounded border border-border hover:bg-muted">{r.enabled ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => remove(r)} className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10">Delete</button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground italic">No rules. Add one above to start filtering.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
